import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@clerk/react';
import { supabase } from '../supabaseClient';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
    const { userId, isSignedIn } = useAuth();

    // 1. User State
    const [user, setUser] = useState({
        name: 'Guest User',
        balance: 150000,
        isVerified: false,
        onboarded: false
    });

    // 2. Bookings State
    const [bookings, setBookings] = useState([
        { id: 101, type: 'Telemedicine', provider: 'Dr. Adewale K. Solomon', time: 'Today, 2:30 PM', status: 'Confirmed', amount: 5000 },
        { id: 102, type: 'Private Tutor', provider: 'Mr. John Obi', time: 'Feb 19, 4:00 PM', status: 'Pending', amount: 3000 },
    ]);

    // 3. Transactions List
    const [transactions, setTransactions] = useState([]);

    // Persist onboarding status
    useEffect(() => {
        const onboarded = localStorage.getItem('ez_onboarded');
        if (onboarded) setUser(prev => ({ ...prev, onboarded: true }));
    }, []);

    // Load real user data and wallet from Supabase under Clerk authentication
    useEffect(() => {
        const fetchUserData = async () => {
            if (!isSignedIn || !userId) return;
            try {
                // Query profile
                const { data: profile, error: profileError } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', userId)
                    .single();
                
                if (profileError) throw profileError;

                // Query wallet
                const { data: wallet, error: walletError } = await supabase
                    .from('wallets')
                    .select('*')
                    .eq('id', userId)
                    .single();

                if (walletError) throw walletError;

                setUser(prev => ({
                    ...prev,
                    name: profile.display_name,
                    balance: parseFloat(wallet.balance),
                    isVerified: profile.is_verified,
                    role: profile.role
                }));
            } catch (err) {
                console.warn('[AppContext] Failed to load Supabase profile/wallet, using mock fallback:', err);
            }
        };

        const fetchBookings = async () => {
            if (!isSignedIn || !userId) return;
            try {
                const { data, error } = await supabase
                    .from('bookings')
                    .select(`
                        id,
                        service_type,
                        status,
                        amount,
                        scheduled_at,
                        provider:providers (
                            full_name
                        )
                    `)
                    .eq('customer_id', userId)
                    .order('created_at', { ascending: false });

                if (error) throw error;
                if (data && data.length > 0) {
                    setBookings(data.map(b => ({
                        id: b.id,
                        type: b.service_type === 'airport_pickup' ? 'Airport Pickup' : b.service_type,
                        provider: b.provider?.full_name || 'Assigned Driver',
                        time: new Date(b.scheduled_at).toLocaleString(),
                        status: b.status.charAt(0).toUpperCase() + b.status.slice(1),
                        amount: parseFloat(b.amount)
                    })));
                }
            } catch (err) {
                console.warn('[AppContext] Failed to load Supabase bookings, using mock fallback:', err);
            }
        };

        if (isSignedIn) {
            fetchUserData();
            fetchBookings();
        }
    }, [isSignedIn, userId]);

    // Helper: Add a new booking
    const addBooking = (newBooking) => {
        setBookings(prev => [newBooking, ...prev]);
        setUser(prev => ({ ...prev, balance: prev.balance - newBooking.amount }));
        setTransactions(prev => [{
            id: Date.now(),
            type: 'Debit',
            category: newBooking.type,
            amount: newBooking.amount,
            date: new Date().toISOString()
        }, ...prev]);
    };

    // Helper: Update Wallet Balance
    const updateBalance = (amount) => {
        setUser(prev => ({ ...prev, balance: prev.balance + amount }));
    };

    // Helper: Set Verification Status (Admin Action)
    const setVerified = (status) => {
        setUser(prev => ({ ...prev, isVerified: status }));
    };

    return (
        <AppContext.Provider value={{ 
            user, 
            setUser, 
            bookings, 
            addBooking, 
            transactions, 
            updateBalance, 
            setVerified 
        }}>
            {children}
        </AppContext.Provider>
    );
};

export const useApp = () => useContext(AppContext);
